import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import type { ScopedContext } from './scopedCapabilityResolver';
import {
  assertProjectionMatchesRequest,
  authorityProjectionRequestKey,
  AuthorityProjectionValidationError,
  fetchCurrentAuthorityProjection,
} from '@/services/AuthorityProjectionService';
import type {
  AuthorityProjection,
  AuthorityProjectionRequest,
  AuthorityProjectionState,
  AuthorityProjectionUnavailableCode,
} from '@/types/authorityProjection';

export interface AuthorityProjectionRequestToken {
  readonly generation: number;
}

export type AuthorityProjectionLoader = (
  request: AuthorityProjectionRequest,
) => Promise<AuthorityProjection>;

export type AuthorityProjectionBoundaryOutcome =
  | 'loading'
  | 'confirmed'
  | 'denied'
  | 'unavailable';

/**
 * A browser projection is presentation state, not a durable grant. Keep its
 * reuse window deliberately short and re-read the server after focus changes.
 */
export const AUTHORITY_PROJECTION_MAX_AGE_MS = 60_000;
const AUTHORITY_PROJECTION_REFRESH_EVENT = 'ax1:authority-projection-refresh';

/** Notify the active route owner after an authority-bearing command succeeds. */
export function requestAuthorityProjectionRefresh(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTHORITY_PROJECTION_REFRESH_EVENT));
  }
}

export function authorityProjectionExpiresAt(
  projection: AuthorityProjection,
  validatedAt: number,
): number {
  const expiries = [
    validatedAt + AUTHORITY_PROJECTION_MAX_AGE_MS,
    Date.parse(projection.computedAt) + AUTHORITY_PROJECTION_MAX_AGE_MS,
    ...projection.authorityBasis
      .map((basis) => basis.effectiveUntil ? Date.parse(basis.effectiveUntil) : Number.POSITIVE_INFINITY),
  ].filter(Number.isFinite);
  return Math.min(...expiries);
}

export function isAuthorityProjectionFresh(
  projection: AuthorityProjection,
  validatedAt: number,
  now = Date.now(),
): boolean {
  return authorityProjectionExpiresAt(projection, validatedAt) > now;
}

const initialState = (): AuthorityProjectionState => ({
  status: 'unavailable',
  request: null,
  projection: null,
  error: {
    code: 'not_started',
    message: 'Authority projection has not been requested.',
  },
});

/**
 * Keep policy denial distinct from an unavailable authority service.  A stale
 * request is loading (and therefore cannot render children) until the exact
 * route request replaces it.
 */
export function authorityProjectionBoundaryOutcome(
  state: AuthorityProjectionState,
  request: AuthorityProjectionRequest,
  now = Date.now(),
): AuthorityProjectionBoundaryOutcome {
  if (!state.request
    || authorityProjectionRequestKey(state.request) !== authorityProjectionRequestKey(request)
    || state.status === 'loading'
    || (state.status === 'unavailable' && state.error.code === 'not_started')) {
    return 'loading';
  }
  if ((state.status === 'confirmed' || state.status === 'denied')
    && !isAuthorityProjectionFresh(state.projection, state.validatedAt, now)) {
    return 'loading';
  }
  return state.status;
}

/**
 * Existing UI call sites still use the scoped-resolver vocabulary while the
 * database capability bundles use read/update identifiers.  Keep the bridge
 * deliberately small and semantic: every entry is an exact requirement, not
 * a prefix, wildcard, or role-derived alias.  Capabilities without an entry
 * must match the server identifier byte-for-byte.
 */
const CONNECTED_CAPABILITY_REQUIREMENTS: Readonly<Record<string, readonly string[]>> = {
  'workspace.view': ['workspace.read'],
  // Workspace list routes are discovery shells. Their records remain filtered
  // by server RLS/RPC; entering the shell requires the exact projected
  // workspace.read capability and never a browser-authored role.
  'workspace.projects.list': ['workspace.read'],
  'workspace.tasks.list': ['workspace.read'],
  'workspace.files.list': ['workspace.read'],
  'project.view': ['project.read'],
  'project.edit': ['project.update'],
  'project.manage': ['project.update', 'project.members.manage'],
  'project.participant.view': ['project.read'],
  'project.participant.manage': ['project.members.manage'],
  'task.create': ['project.update'],
  'document.upload': ['project.update'],
  'document.update': ['project.update'],
  'investment.view': ['investment.read'],
};

export function projectionRequirementsForCapability(capability: string): readonly string[] {
  return CONNECTED_CAPABILITY_REQUIREMENTS[capability] ?? [capability];
}

function resourceFromScopedContext(
  capability: string,
  context: ScopedContext,
): AuthorityProjectionRequest['resource'] {
  if (capability.startsWith('project.') || capability.startsWith('folder.')) {
    return context.projectId ? { type: 'project', id: context.projectId } : null;
  }
  if (capability.startsWith('investment.')) {
    return context.investmentId ? { type: 'investment', id: context.investmentId } : null;
  }
  if (capability.startsWith('milestone.')) {
    return context.milestoneId ? { type: 'milestone', id: context.milestoneId } : null;
  }
  if (capability.startsWith('task.')) {
    if (context.taskId) return { type: 'task', id: context.taskId };
    return context.projectId ? { type: 'project', id: context.projectId } : null;
  }
  if (capability.startsWith('document.')) {
    if (context.documentId) return { type: 'document', id: context.documentId };
    if (context.vaultId) return { type: 'vault', id: context.vaultId };
    return context.projectId ? { type: 'project', id: context.projectId } : null;
  }
  if (capability.startsWith('vault.')) {
    if (context.vaultId) return { type: 'vault', id: context.vaultId };
    return context.projectId ? { type: 'project', id: context.projectId } : null;
  }
  if (capability.startsWith('company.')) {
    return context.companyId ? { type: 'company', id: context.companyId } : null;
  }
  return null;
}

function scopedContextMatches(
  capability: string,
  request: AuthorityProjectionRequest,
  projection: AuthorityProjection,
  context: ScopedContext | undefined,
): boolean {
  // Connected authority is never inferred from a context-free check.
  if (!context) return false;
  if (context.organizationId && context.organizationId !== projection.scope.organizationId) return false;
  if (!context.workspaceId || context.workspaceId !== projection.scope.workspaceId) return false;

  // A resource projection may also carry workspace capabilities (for example,
  // an organization admin viewing a project).  Those remain safe to use only
  // for the exact projected workspace.
  if (capability.startsWith('workspace.')) return true;

  const expectedResource = resourceFromScopedContext(capability, context);
  if (!expectedResource) return false;

  // A workspace projection can carry organization-admin project capabilities.
  // Honor those only when the server explicitly returned the exact capability;
  // scoped participants receive no project capability in a workspace request
  // and therefore still need a resource-exact projection.
  if (capability.startsWith('project.')
    && request.resource === null
    && projection.scope.resource === null) {
    return true;
  }
  if (!request.resource || !projection.scope.resource) return false;
  return request.resource.type === expectedResource.type
    && request.resource.id === expectedResource.id
    && projection.scope.resource.type === expectedResource.type
    && projection.scope.resource.id === expectedResource.id;
}

export class AuthorityProjectionSession {
  private generation = 0;
  private state: AuthorityProjectionState = initialState();
  private readonly listeners = new Set<() => void>();

  getSnapshot = (): AuthorityProjectionState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private publish(state: AuthorityProjectionState): void {
    this.state = state;
    for (const listener of this.listeners) listener();
  }

  /** Clears prior authority synchronously before any new projection can resolve. */
  begin(request: AuthorityProjectionRequest): AuthorityProjectionRequestToken {
    const token = { generation: ++this.generation };
    this.publish({ status: 'loading', request, projection: null, error: null });
    return token;
  }

  clear(
    code: AuthorityProjectionUnavailableCode = 'scope_cleared',
    message = 'Authority scope was cleared.',
  ): void {
    this.generation += 1;
    this.publish({ status: 'unavailable', request: null, projection: null, error: { code, message } });
  }

  cancel(token: AuthorityProjectionRequestToken): boolean {
    if (token.generation !== this.generation) return false;
    this.clear('scope_cleared', 'Authority projection request was cancelled.');
    return true;
  }

  /** Clear the active scope even when a same-scope refresh replaced its token. */
  clearRequest(request: AuthorityProjectionRequest): boolean {
    if (!this.state.request
      || authorityProjectionRequestKey(this.state.request) !== authorityProjectionRequestKey(request)) {
      return false;
    }
    this.clear('scope_cleared', 'Authority projection scope was closed.');
    return true;
  }

  accept(token: AuthorityProjectionRequestToken, projection: AuthorityProjection): boolean {
    if (token.generation !== this.generation || this.state.status !== 'loading') return false;
    try {
      assertProjectionMatchesRequest(projection, this.state.request);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Authority projection did not match the active scope.';
      this.publish({
        status: 'unavailable',
        request: this.state.request,
        projection: null,
        error: { code: 'invalid_projection', message },
      });
      return false;
    }

    this.publish({
      status: projection.decision.allowed ? 'confirmed' : 'denied',
      request: this.state.request,
      projection,
      validatedAt: Date.now(),
      error: null,
    });
    return true;
  }

  fail(token: AuthorityProjectionRequestToken, error: unknown): boolean {
    if (token.generation !== this.generation || this.state.status !== 'loading') return false;
    const message = error instanceof Error ? error.message : 'Authority projection is unavailable.';
    const code: AuthorityProjectionUnavailableCode = error instanceof AuthorityProjectionValidationError
      ? 'invalid_projection'
      : 'rpc_unavailable';
    this.publish({
      status: 'unavailable',
      request: this.state.request,
      projection: null,
      error: { code, message },
    });
    return true;
  }

  async load(
    request: AuthorityProjectionRequest,
    loader: AuthorityProjectionLoader = fetchCurrentAuthorityProjection,
  ): Promise<boolean> {
    const token = this.begin(request);
    try {
      return this.accept(token, await loader(request));
    } catch (error) {
      return this.fail(token, error);
    }
  }

  /** Exact capability membership only; no prefixes, role fallbacks, or wildcards. */
  can(capability: string, context?: ScopedContext): boolean {
    if (this.state.status !== 'confirmed'
      || !isAuthorityProjectionFresh(this.state.projection, this.state.validatedAt)
      || !scopedContextMatches(capability, this.state.request, this.state.projection, context)) {
      return false;
    }
    const projected = new Set<string>(this.state.projection.capabilities);
    return projectionRequirementsForCapability(capability)
      .every((required) => projected.has(required));
  }
}

export const authorityProjectionSession = new AuthorityProjectionSession();

export function useAuthorityProjectionState(): AuthorityProjectionState {
  return useSyncExternalStore(
    authorityProjectionSession.subscribe,
    authorityProjectionSession.getSnapshot,
    authorityProjectionSession.getSnapshot,
  );
}

/**
 * Own one exact route projection. Scope changes clear the previous projection
 * synchronously; expiry and foregrounding force a fresh server read.
 */
export function useAuthorityProjectionLifecycle({
  request,
  enabled,
  authorityVersion,
}: {
  request: AuthorityProjectionRequest | null;
  enabled: boolean;
  authorityVersion: number;
}) {
  const state = useAuthorityProjectionState();
  const [retryVersion, setRetryVersion] = useState(0);
  const lastForegroundRefresh = useRef(0);

  const retry = useCallback(() => {
    setRetryVersion((version) => version + 1);
  }, []);

  useLayoutEffect(() => {
    if (!enabled || !request) {
      authorityProjectionSession.clear('scope_cleared', 'No confirmed authority scope is active.');
      return;
    }

    const token = authorityProjectionSession.begin(request);
    void fetchCurrentAuthorityProjection(request)
      .then((projection) => authorityProjectionSession.accept(token, projection))
      .catch((error: unknown) => authorityProjectionSession.fail(token, error));

    return () => {
      authorityProjectionSession.clearRequest(request);
    };
  }, [authorityVersion, enabled, request, retryVersion]);

  const outcome = enabled && request
    ? authorityProjectionBoundaryOutcome(state, request)
    : null;

  useEffect(() => {
    if (!enabled || !request || (state.status !== 'confirmed' && state.status !== 'denied')) return;
    if (authorityProjectionRequestKey(state.request) !== authorityProjectionRequestKey(request)) return;

    const delay = Math.max(0, authorityProjectionExpiresAt(state.projection, state.validatedAt) - Date.now());
    const timer = window.setTimeout(retry, Math.min(delay, 2_147_483_647));
    return () => window.clearTimeout(timer);
  }, [enabled, request, retry, state]);

  useEffect(() => {
    if (!enabled || !request || typeof window === 'undefined' || typeof document === 'undefined') return;

    const refreshOnForeground = () => {
      if (document.visibilityState === 'hidden') return;
      const now = Date.now();
      // Browsers commonly dispatch visibilitychange and focus together.
      if (now - lastForegroundRefresh.current < 500) return;
      lastForegroundRefresh.current = now;
      retry();
    };

    window.addEventListener('focus', refreshOnForeground);
    window.addEventListener(AUTHORITY_PROJECTION_REFRESH_EVENT, retry);
    document.addEventListener('visibilitychange', refreshOnForeground);
    return () => {
      window.removeEventListener('focus', refreshOnForeground);
      window.removeEventListener(AUTHORITY_PROJECTION_REFRESH_EVENT, retry);
      document.removeEventListener('visibilitychange', refreshOnForeground);
    };
  }, [enabled, request, retry]);

  return { state, outcome, retry } as const;
}
